/* eslint-disable @typescript-eslint/no-floating-promises */
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import t from 'ember-intl/helpers/t';
import type IntlService from 'ember-intl/services/intl';
import PpsSections from 'parog-web/components/consultation/report-form/content/pps-sections';
import type ForcJournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import { type Signature as ForcProviderSignature } from 'parog-web/components/forc/provider';
import ConsultationReportWrittenJournalEvent from 'parog-web/models/consultation-report-written-journal-event';
import type ConsultationReportWrittenQuestion from 'parog-web/models/consultation/consultation-report-written-question';
import type Patient from 'parog-web/models/patient';

interface ConsultationReportFormContentComponentArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forcComponent: ForcJournalEventBasedComponent<any>;
  onUpdateQuestion?: () => void;
  setCurrentQuestions: (questions: ConsultationReportWrittenQuestion[]) => void;
  // When the consultation type comes from the dashboard, it is not in the dropdown list so it cannot be changed
  shouldHideConsultationType?: boolean;
  patient: Patient;
  isDisplayedFromPanel?: boolean;
  questions: ConsultationReportWrittenQuestion[];
  form: ForcProviderSignature['Blocks']['content'][0];
}

interface QuestionWithAnswer {
  question: ConsultationReportWrittenQuestion;
  answer: string;
}

export default class ConsultationReportFormContentComponent extends Component<ConsultationReportFormContentComponentArgs> {
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked declare questions: ConsultationReportWrittenQuestion[];

  get consultationTypes(): string[] {
    const consultationReportTypes =
      this.#patient.followUpReasonRecord?.consultationReportTypes || [];

    return consultationReportTypes.map(type => {
      return this.intl.t(`consultation-report-types.${type.id}`);
    });
  }

  get consultationTypesFromFrequencies(): string[] {
    return this.#patient.consultationFrequencies
      .filter(type => type.enabled)
      .map(type => type.key);
  }

  get shouldDisplayPPSActionsDropdown(): boolean {
    if (
      this.args.forcComponent.args.patient.followUpReasonRecord
        ?.therapeuticalArea.id != 'mental'
    )
      return false;

    return this.#consultationType?.startsWith('Point de suivi récurrent');
  }

  get #consultationType(): string {
    return this.args.forcComponent.changeset.get('consultationType');
  }

  get #patient(): Patient {
    return this.args.patient;
  }

  constructor(owner: Owner, args: ConsultationReportFormContentComponentArgs) {
    super(owner, args);
    // It is needed to update the questions
    // when the changeset is updated after draft loading
    args.forcComponent.afterLoad = () => {
      this.onConsultationTypeChange();
    };
  }

  onConsultationTypeChange = async (): Promise<void> => {
    if (this.#consultationType == 'Accompagnement thérapeutique') {
      this.args.forcComponent.changeset.set('consultationType', null);
      this.router.transitionTo({
        queryParams: {
          tab: 'write-therapeutic-assistance'
        }
      });
    } else {
      if (this.#consultationType == undefined) {
        this.args.setCurrentQuestions?.([]);
      } else {
        // User can only fill questions
        // we indicated in ConsultationReportWrittenJournalEvent.getQuestions
        if (this.args.forcComponent.isNewMode) {
          const defaultQuestions =
            ConsultationReportWrittenJournalEvent.getQuestions(
              this.#consultationType
            );
          this.args.setCurrentQuestions?.(defaultQuestions);
          // This is needed, otherwise changeset is recomputed when typing the first answer
          // of the first question, and we lose the focus from the textarea
          this.#updateQuestionsFromChangeset(
            defaultQuestions.map(defaultQuestion => {
              return {
                question: defaultQuestion,
                answer: defaultQuestion.answer
              };
            }),
            false
          );
        }
        // If the user is editing the JE
        // it may want to see old questions that
        // are not provided anymore
        else {
          this.args.setCurrentQuestions(
            (
              this.args.forcComponent
                .model as ConsultationReportWrittenJournalEvent
            ).questions
          );
        }
      }
    }
  };

  updateQuestion = (
    question: ConsultationReportWrittenQuestion,
    event: InputEvent
  ): void => {
    this.#updateQuestionsFromChangeset([
      { question, answer: (event.target as HTMLInputElement).value }
    ]);
    this.args.onUpdateQuestion?.();
  };

  // As the `questions` property of the changeset is an array,
  // we have to manage it on our own
  #updateQuestionsFromChangeset = (
    questionsWithAnswer: QuestionWithAnswer[],
    replace = true
  ): void => {
    const changesetQuestions = Object.values(
      Object.assign(
        {},
        this.args.forcComponent.changeset.get('questions')
      ) as ConsultationReportWrittenQuestion[]
    );

    questionsWithAnswer.forEach(questionWithAnswer => {
      const questionToUpdate = changesetQuestions.find(
        question => question.key == questionWithAnswer.question.key
      );
      if (questionToUpdate && replace) {
        questionToUpdate.answer = questionWithAnswer.answer;
      } else {
        if (!questionToUpdate) {
          changesetQuestions.push({
            key: questionWithAnswer.question.key,
            issue: questionWithAnswer.question.issue,
            answer: questionWithAnswer.answer
          });
        }
      }
    });
    this.args.forcComponent.changeset.set('questions', changesetQuestions);
  };

  <template>
    <div class='field'>
      <@form.Date
        @labelName='Date du compte rendu'
        @propertyName='occurredAt'
        @isMandatory={{true}}
      />
    </div>

    {{#unless @shouldHideConsultationType}}
      <div class='field'>
        <@form.Dropdown
          @labelName='Type de consultation ou rendez-vous'
          @allowAdditions={{true}}
          @isMandatory={{true}}
          @onChangesetUpdate={{this.onConsultationTypeChange}}
          @propertyName='consultationType'
          as |dropdown|
        >
          {{#each this.consultationTypes as |type|}}
            <dropdown.Item @key={{type}} @value={{type}} />
          {{/each}}

          {{#each this.consultationTypesFromFrequencies as |type|}}
            <dropdown.Item
              @key={{type}}
              @value={{t (concat 'consultation-history.' type)}}
            />
          {{/each}}
        </@form.Dropdown>
      </div>
    {{/unless}}

    {{! This is mental only }}
    {{#if this.shouldDisplayPPSActionsDropdown}}
      <PpsSections @form={{@form}} />
    {{/if}}
    {{! #endregion }}

    {{#each @questions as |question|}}
      <div class='field'>
        <label>
          {{question.issue}}
          <textarea
            value={{question.answer}}
            {{on 'input' (fn this.updateQuestion question)}}
            rows={{if @isDisplayedFromPanel 20 4}}
            class='ui input'
            data-test-textarea='{{question.key}}'
          ></textarea>
        </label>
      </div>
    {{/each}}
  </template>
}
